/**
 * Question Detail Page
 * 
 * ΔD5.2: Question detail with lineage-safe mutation operations
 * Supports edit, split, merge, ignore operations
 * 
 * Phase E5: AI Drafting UI
 * Supports draft generation with controls for tone, length, and outcome
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getFirestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  getProject,
  getQuestion,
  editQuestion,
  splitQuestion,
  ignoreQuestion,
  createJob,
  listAnswersForQuestion,
  type RbProject,
  type RbQuestion,
  type RbAnswer
} from '@threadlock/data';
import { ROUTES } from '@threadlock/routing';
import { useAuthState } from '../../config/auth';
import { useWorkspaceState } from '../../config/workspace';

export default function QuestionDetailPage() {
  const { projectId, questionId } = useParams<{ projectId: string; questionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthState();
  const { activeWorkspaceId } = useWorkspaceState();
  const userId = user?.uid;
  const workspaceId = activeWorkspaceId;
  const [project, setProject] = useState<RbProject | null>(null);
  const [question, setQuestion] = useState<RbQuestion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Split state
  const [isSplitting, setIsSplitting] = useState(false);
  const [splitTexts, setSplitTexts] = useState<string[]>(['', '']);
  
  // Ignore confirmation
  const [showIgnoreConfirm, setShowIgnoreConfirm] = useState(false);

  // Phase E5: Drafting state
  const [showDrafting, setShowDrafting] = useState(false);
  const [tone, setTone] = useState<'neutral' | 'formal' | 'conservative'>('neutral');
  const [targetLength, setTargetLength] = useState<'short' | 'medium' | 'long'>('medium');
  const [intendedOutcome, setIntendedOutcome] = useState<'informational' | 'compliance' | 'technical'>('informational');
  const [policyInclusion, setPolicyInclusion] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<RbAnswer[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<RbAnswer | null>(null);

  useEffect(() => {
    if (!projectId || !questionId) return;

    const db = getFirestore();

    Promise.all([
      getProject(db, projectId),
      getQuestion(db, questionId),
      listAnswersForQuestion(db, questionId),
    ])
      .then(([proj, q, answersList]) => {
        if (!proj) {
          setError('Project not found');
          setLoading(false);
          return;
        }
        if (!q) {
          setError('Question not found');
          setLoading(false);
          return;
        }

        setProject(proj);
        setQuestion(q);
        setEditText(q.questionText);
        setAnswers(answersList);
        if (answersList.length > 0) {
          setSelectedAnswer(answersList[0]); // Show most recent answer
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('[QuestionDetailPage] Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load question');
        setLoading(false);
      });
  }, [projectId, questionId]);

  const handleEdit = async () => {
    if (!projectId || !questionId || !workspaceId || !userId || !editText.trim()) return;

    setSaving(true);
    try {
      const db = getFirestore();
      const newQuestionId = `q_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      
      await editQuestion(db, newQuestionId, {
        workspaceId,
        projectId,
        priorQuestionId: questionId,
        newQuestionText: editText.trim(),
        createdByUserId: userId,
      });

      // Create audit job
      const jobId = `job_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      await createJob(db, jobId, {
        workspaceId,
        type: 'project',
        action: 'question_edited',
        status: 'ok',
        message: 'Question edited',
        meta: {
          projectId,
          questionId: newQuestionId,
          priorQuestionId: questionId,
          lineageRootId: question?.lineageRootId,
        },
        createdByUserId: userId,
      });

      // Navigate back to questions list
      navigate(ROUTES.PROJECT_QUESTIONS.replace(':projectId', projectId));
    } catch (err) {
      console.error('[QuestionDetailPage] Edit failed:', err);
      alert(err instanceof Error ? err.message : 'Failed to edit question');
      setSaving(false);
    }
  };

  const handleSplit = async () => {
    if (!projectId || !questionId || !workspaceId || !userId) return;

    const validSplitTexts = splitTexts.filter(t => t.trim().length > 0);
    if (validSplitTexts.length < 2) {
      alert('Please provide at least 2 question texts for split');
      return;
    }

    setSaving(true);
    try {
      const db = getFirestore();
      const childIds = validSplitTexts.map(() => 
        `q_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
      );
      
      await splitQuestion(db, childIds, {
        workspaceId,
        projectId,
        parentQuestionId: questionId,
        splitTexts: validSplitTexts,
        createdByUserId: userId,
      });

      // Create audit job
      const jobId = `job_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      await createJob(db, jobId, {
        workspaceId,
        type: 'project',
        action: 'question_split',
        status: 'ok',
        message: `Question split into ${validSplitTexts.length} parts`,
        meta: {
          projectId,
          priorQuestionId: questionId,
          relatedQuestionIds: childIds,
          lineageRootId: question?.lineageRootId,
        },
        createdByUserId: userId,
      });

      // Navigate back to questions list
      navigate(ROUTES.PROJECT_QUESTIONS.replace(':projectId', projectId));
    } catch (err) {
      console.error('[QuestionDetailPage] Split failed:', err);
      alert(err instanceof Error ? err.message : 'Failed to split question');
      setSaving(false);
    }
  };

  const handleIgnore = async () => {
    if (!projectId || !questionId || !workspaceId || !userId) return;

    setSaving(true);
    try {
      const db = getFirestore();
      const newQuestionId = `q_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      
      await ignoreQuestion(db, newQuestionId, {
        workspaceId,
        projectId,
        questionId,
        createdByUserId: userId,
      });

      // Create audit job
      const jobId = `job_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      await createJob(db, jobId, {
        workspaceId,
        type: 'project',
        action: 'question_ignored',
        status: 'ok',
        message: 'Question marked as ignored',
        meta: {
          projectId,
          questionId: newQuestionId,
          priorQuestionId: questionId,
          lineageRootId: question?.lineageRootId,
        },
        createdByUserId: userId,
      });

      // Navigate back to questions list
      navigate(ROUTES.PROJECT_QUESTIONS.replace(':projectId', projectId));
    } catch (err) {
      console.error('[QuestionDetailPage] Ignore failed:', err);
      alert(err instanceof Error ? err.message : 'Failed to ignore question');
      setSaving(false);
    }
  };

  // Phase E5: Generate draft
  const handleGenerateDraft = async () => {
    if (!projectId || !questionId || !workspaceId) return;

    setDrafting(true);
    setDraftError(null);

    try {
      const functions = getFunctions();
      const rbDraftAnswer = httpsCallable(functions, 'rbDraftAnswer');
      
      const result = await rbDraftAnswer({
        workspaceId,
        projectId,
        questionId,
        tone,
        targetLength,
        intendedOutcome,
        policyInclusion,
      });

      const data = result.data as any;
      
      if (data.success && data.answerId) {
        // Reload answers to show the new draft
        const db = getFirestore();
        const updatedAnswers = await listAnswersForQuestion(db, questionId);
        setAnswers(updatedAnswers);
        if (updatedAnswers.length > 0) {
          setSelectedAnswer(updatedAnswers[0]); // Show most recent
        }
        setShowDrafting(false);
      } else {
        setDraftError('Failed to generate draft');
      }
    } catch (err: any) {
      console.error('[QuestionDetailPage] Draft generation failed:', err);
      setDraftError(err.message || 'Failed to generate draft');
    } finally {
      setDrafting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-neutral-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error || !project || !question) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error || 'Question not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Question Details</h1>
            <p className="mt-1 text-sm text-neutral-600">
              <Link
                to={ROUTES.PROJECT_QUESTIONS.replace(':projectId', project.id)}
                className="text-blue-600 hover:text-blue-700"
              >
                {project.title}
              </Link>
            </p>
          </div>
          <Link
            to={ROUTES.PROJECT_QUESTIONS.replace(':projectId', project.id)}
            className="inline-flex items-center text-sm text-neutral-600 hover:text-neutral-900"
          >
            ← Back to Questions
          </Link>
        </div>

        {/* Question info */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                question.state === 'active'
                  ? 'bg-green-100 text-green-800'
                  : question.state === 'ignored'
                  ? 'bg-neutral-100 text-neutral-600'
                  : 'bg-neutral-50 text-neutral-500'
              }`}
            >
              {question.state}
            </span>
            <span className="text-xs text-neutral-500">
              Operation: {question.operation}
            </span>
            {question.operation !== 'extract' && (
              <span className="text-xs text-neutral-500">
                (Lineage root: {question.lineageRootId})
              </span>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-neutral-700 mb-1">Current Question Text</h3>
              <p className="text-sm text-neutral-900 p-3 bg-neutral-50 rounded whitespace-pre-wrap">
                {question.questionText}
              </p>
            </div>

            {question.extractedText !== question.questionText && (
              <div>
                <h3 className="text-sm font-medium text-neutral-700 mb-1">Original Extracted Text</h3>
                <p className="text-sm text-neutral-600 p-3 bg-neutral-50 rounded whitespace-pre-wrap">
                  {question.extractedText}
                </p>
              </div>
            )}

            <div className="text-xs text-neutral-500">
              Created: {question.createdAt.toDate().toLocaleString()}
            </div>
          </div>
        </div>

        {/* Phase E5: AI Drafting Section */}
        {question.state === 'active' && (
          <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-neutral-900 mb-4">AI Draft Generation</h2>
            
            {/* Existing drafts */}
            {answers.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-neutral-700 mb-2">
                  Previous Drafts ({answers.length})
                </h3>
                <div className="flex gap-2 mb-4 overflow-x-auto">
                  {answers.map((answer, idx) => (
                    <button
                      key={answer.id}
                      onClick={() => setSelectedAnswer(answer)}
                      className={`px-3 py-1 text-xs font-medium rounded whitespace-nowrap ${
                        selectedAnswer?.id === answer.id
                          ? 'bg-blue-100 text-blue-800 border-2 border-blue-500'
                          : 'bg-neutral-100 text-neutral-700 border border-neutral-300 hover:bg-neutral-200'
                      }`}
                    >
                      Draft {answers.length - idx} ({answer.createdAt.toDate().toLocaleDateString()})
                    </button>
                  ))}
                </div>
                
                {selectedAnswer && (
                  <div className="space-y-4 p-4 bg-neutral-50 rounded-lg">
                    <div>
                      <h4 className="text-sm font-medium text-neutral-700 mb-2">Draft Text</h4>
                      <p className="text-sm text-neutral-900 whitespace-pre-wrap">
                        {selectedAnswer.draftText}
                      </p>
                    </div>
                    
                    {selectedAnswer.citations.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-neutral-700 mb-2">Citations ({selectedAnswer.citations.length})</h4>
                        <div className="space-y-2">
                          {selectedAnswer.citations.map((citation, idx) => {
                            // Truncate at word boundary if needed
                            const excerpt = citation.excerpt.length > 150 
                              ? citation.excerpt.substring(0, 150).trim() + '...'
                              : citation.excerpt;
                            return (
                              <div key={idx} className="p-2 bg-white rounded border border-neutral-200">
                                <p className="text-xs text-neutral-600 italic">"{excerpt}"</p>
                                <p className="text-xs text-neutral-500 mt-1">
                                  Source: {citation.documentId} (Chunk: {citation.chunkId})
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {selectedAnswer.warnings.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-amber-700 mb-2">⚠️ Warnings</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {selectedAnswer.warnings.map((warning, idx) => (
                            <li key={idx} className="text-sm text-amber-800">{warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {selectedAnswer.assumptions.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-neutral-700 mb-2">Assumptions</h4>
                        <ul className="list-disc list-inside space-y-1">
                          {selectedAnswer.assumptions.map((assumption, idx) => (
                            <li key={idx} className="text-sm text-neutral-600">{assumption}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-neutral-500 pt-2 border-t border-neutral-200">
                      <span>Confidence: {(selectedAnswer.confidenceScore * 100).toFixed(0)}%</span>
                      <span>AI Run: {selectedAnswer.aiRunId}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Generate new draft */}
            {!showDrafting ? (
              <button
                onClick={() => setShowDrafting(true)}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Generate New Draft
              </button>
            ) : (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-sm font-semibold text-neutral-900">Draft Controls</h3>
                
                {/* Tone */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Tone</label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value as 'neutral' | 'formal' | 'conservative')}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="neutral">Neutral (clear, straightforward)</option>
                    <option value="formal">Formal (professional, structured)</option>
                    <option value="conservative">Conservative (careful, cautious)</option>
                  </select>
                </div>
                
                {/* Target Length */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Target Length</label>
                  <select
                    value={targetLength}
                    onChange={(e) => setTargetLength(e.target.value as 'short' | 'medium' | 'long')}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="short">Short (1-2 paragraphs)</option>
                    <option value="medium">Medium (3-4 paragraphs)</option>
                    <option value="long">Long (5+ paragraphs with detailed analysis)</option>
                  </select>
                </div>
                
                {/* Intended Outcome */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Intended Outcome</label>
                  <select
                    value={intendedOutcome}
                    onChange={(e) => setIntendedOutcome(e.target.value as 'informational' | 'compliance' | 'technical')}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="informational">Informational (explain and educate)</option>
                    <option value="compliance">Compliance (requirements and obligations)</option>
                    <option value="technical">Technical (specifications and procedures)</option>
                  </select>
                </div>
                
                {/* Policy Inclusion */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="policyInclusion"
                    checked={policyInclusion}
                    onChange={(e) => setPolicyInclusion(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 rounded"
                  />
                  <label htmlFor="policyInclusion" className="ml-2 text-sm text-neutral-700">
                    Include policy references
                  </label>
                </div>
                
                {draftError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                    {draftError}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <button
                    onClick={handleGenerateDraft}
                    disabled={drafting}
                    className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-neutral-300 disabled:cursor-not-allowed"
                  >
                    {drafting ? 'Generating...' : 'Generate Draft'}
                  </button>
                  <button
                    onClick={() => {
                      setShowDrafting(false);
                      setDraftError(null);
                    }}
                    disabled={drafting}
                    className="px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {question.state === 'active' && (
          <div className="space-y-6">
            {/* Edit */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Edit Question</h3>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Edit Text →
                </button>
              ) : (
                <div className="space-y-3">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleEdit}
                      disabled={saving || !editText.trim()}
                      className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-neutral-300 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Saving...' : 'Save Edit'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setEditText(question.questionText);
                      }}
                      disabled={saving}
                      className="px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Split */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Split Question</h3>
              {!isSplitting ? (
                <button
                  onClick={() => setIsSplitting(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Split into Multiple →
                </button>
              ) : (
                <div className="space-y-3">
                  {splitTexts.map((text, index) => (
                    <div key={index}>
                      <label className="block text-xs font-medium text-neutral-700 mb-1">
                        Question {index + 1}
                      </label>
                      <textarea
                        value={text}
                        onChange={(e) => {
                          const newSplitTexts = [...splitTexts];
                          newSplitTexts[index] = e.target.value;
                          setSplitTexts(newSplitTexts);
                        }}
                        rows={2}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ))}
                  <button
                    onClick={() => setSplitTexts([...splitTexts, ''])}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    + Add another question
                  </button>
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={handleSplit}
                      disabled={saving}
                      className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-neutral-300 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Splitting...' : 'Save Split'}
                    </button>
                    <button
                      onClick={() => {
                        setIsSplitting(false);
                        setSplitTexts(['', '']);
                      }}
                      disabled={saving}
                      className="px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Ignore */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Mark as Non-Response</h3>
              {!showIgnoreConfirm ? (
                <button
                  onClick={() => setShowIgnoreConfirm(true)}
                  className="text-sm text-neutral-600 hover:text-neutral-900 font-medium"
                >
                  Ignore this question →
                </button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-neutral-600">
                    This will mark the question as non-response item. This action creates a new lineage node.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleIgnore}
                      disabled={saving}
                      className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-neutral-600 hover:bg-neutral-700 disabled:bg-neutral-300 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Ignoring...' : 'Confirm Ignore'}
                    </button>
                    <button
                      onClick={() => setShowIgnoreConfirm(false)}
                      disabled={saving}
                      className="px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {question.state === 'ignored' && (
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6">
            <p className="text-sm text-neutral-600">
              This question is marked as ignored (non-response item). No further actions available.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}